import { Radio } from 'antd';
import styled from '@emotion/styled';
import FormField from 'components/FormField';
import useSmartField from 'hooks/useSmartField';
import { ReactComponent as SoloConfigSvg } from './icon/soloconfig.svg';
import StoreSelect from './selectors/StoreSelect';
import ItemSelect from './selectors/ItemSelect';
import DtScopeSelect from './selectors/DtScopeSelect';
import { ExcludeTag } from '@knowledge/knowledges/general/KnowledgeListForm/ConditionRenderer';
import { useTranslate } from '@tolgee/react';
import { useLatest, useUpdateEffect } from 'ahooks';
import { useCallback } from 'react';
import { queryItemListByInternalItemIds } from '@knowledge/common/components/ItemSelectV2/api';
import { Store } from 'components/StoreScopes';
import { useLLM } from '@knowledge/common/hooks/useGroup';

type ConfigurationItemProps = {
  fieldNamePrefix: string;
  stores?: Store[];
  storeScopesField?: {
    name: string;
    storeType: Knowledge.StoreType;
    value: General.StoreScope[];
    onChange: (value: General.StoreScope[]) => void;
  };
  validateStore?: boolean;
};

type FV = Knowledge.Condition;

function ConfigurationItem(props: ConfigurationItemProps) {
  const { fieldNamePrefix, stores, storeScopesField, validateStore = true } = props;
  const { t } = useTranslate();
  const LLM = useLLM();
  const { field: storeTypeField } = useSmartField<FV['storeScope']['storeType']>(
    `${fieldNamePrefix}.storeScope.storeType`,
  );
  const { field: storeScopeField, helpers: storeScopeHelpers } = useSmartField<
    FV['storeScope']['storeScope']
  >(`${fieldNamePrefix}.storeScope.storeScope`);
  const { field: specifiedItemsField, helpers: specifiedItemsHelpers } = useSmartField<
    FV['itemScope']['specifiedItems']
  >(`${fieldNamePrefix}.itemScope.specifiedItems`);
  const { field: excludeField } = useSmartField<FV['itemScope']['exclude']>(
    `${fieldNamePrefix}.itemScope.exclude`,
  );
  const specifiedItemsRef = useLatest(specifiedItemsField.value);

  // 店铺ids变化之后，需要清空非该店铺的指定商品
  const onIdsChange = useCallback((ids) => {
    const itemInternalIds = specifiedItemsRef.current?.map((e) => e.itemInternalId);
    if (!itemInternalIds?.length) {
      return;
    }
    queryItemListByInternalItemIds(itemInternalIds || []).then((res) => {
      const list = res.itemList || [];
      const newSpecifiedItemsIds = list
        .filter((e) => ids?.some((id: any) => String(id) === e.internalStoreId))
        .map((e) => e.internalItemId);
      specifiedItemsHelpers.setValue(
        specifiedItemsRef.current?.filter((e) => newSpecifiedItemsIds.includes(e.itemInternalId)),
      );
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useUpdateEffect(() => {
    specifiedItemsHelpers.setValue([]);
    storeScopeHelpers.setValue([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storeTypeField.value]);

  const storeScopesFieldName = storeScopesField?.name || `${fieldNamePrefix}.storeScope.storeScope`;

  return (
    <Container>
      <Header>
        <div>条件</div>
        <div>
          <SoloConfigSvg />
          <span>单独配置</span>
        </div>
      </Header>

      <Items>
        <Item>
          <ItemTitle>{t('marketing.店铺')}:&nbsp;</ItemTitle>
          <ItemArea>
            <div>
              <FormField
                name={storeScopesFieldName}
                validateTrigger={['onChange']}
                validate={
                  validateStore
                    ? (value) => {
                        if (value?.length === 0) {
                          return t('storeGroup.请选择店铺');
                        }
                      }
                    : undefined
                }
              >
                {({ field }) => {
                  return (
                    <StoreSelect
                      storeType={storeScopesField?.storeType || storeTypeField.value}
                      editable
                      dataSource={stores}
                      value={storeScopesField?.value || field.value}
                      onChange={storeScopesField?.onChange || field.onChange}
                      onIdsChange={onIdsChange}
                    />
                  );
                }}
              </FormField>
            </div>
          </ItemArea>
        </Item>
        <Item>
          <ItemTitle>{t('multiTimeSelect.时间')}:&nbsp;</ItemTitle>
          <ItemArea>
            <DtScopeSelect fieldNamePrefix={`${fieldNamePrefix}.dtScope`} />
          </ItemArea>
        </Item>
        <Item>
          <ItemTitle>{t('knowledge.商品')}:&nbsp;</ItemTitle>
          <ItemArea>
            <FormField name={`${fieldNamePrefix}.itemScope.filterCode`}>
              {({ field }) => {
                return (
                  <SpuSelector>
                    <Radio.Group value={field.value} onChange={field.onChange}>
                      <Radio value="ITEM_ALL">{t('aide.全部商品')}</Radio>
                      <Radio value="ITEM_SPECIFIED">{t('aide.指定商品')}</Radio>
                    </Radio.Group>
                    {field.value === 'ITEM_SPECIFIED' && (
                      <div className={selectInfoCss}>
                        <FormField
                          name={`${fieldNamePrefix}.itemScope.specifiedItems`}
                          validateTrigger={['onSubmit']}
                          validate={(value) => {
                            if (value?.length === 0) {
                              return t('knowledge.请选择商品');
                            }
                          }}
                        >
                          {({ field: itemField }) => {
                            return (
                              <ItemSelect
                                storeScopes={storeScopesField?.value || storeScopeField.value}
                                storeType={storeScopesField?.storeType || storeTypeField.value}
                                value={itemField.value}
                                onChange={itemField.onChange}
                              />
                            );
                          }}
                        </FormField>
                      </div>
                    )}
                    {excludeField.value?.length > 0 && field.value !== 'ITEM_SPECIFIED' && (
                      <div className="mt-[4px] overflow-hidden">
                        <ExcludeTag wrapTag value={excludeField.value} />
                      </div>
                    )}
                  </SpuSelector>
                );
              }}
            </FormField>
          </ItemArea>
        </Item>
        {!LLM && (
          <Item>
            <ItemTitle>
              {t('aide.现货')}&{t('aide.预售')}：
            </ItemTitle>
            <ItemArea>
              <FormField name={`${fieldNamePrefix}.itemScope.saleTypes`}>
                {({ field, helpers }) => {
                  return (
                    <Radio.Group
                      value={JSON.stringify(field.value)}
                      onChange={(e) => helpers.setValue(JSON.parse(e.target.value))}
                    >
                      <Radio value={JSON.stringify(['ONSALE', 'PREORDER'])}>
                        {t('feedback.全部')}
                      </Radio>
                      <Radio value={JSON.stringify(['ONSALE'])}>{t('aide.现货')}</Radio>
                      <Radio value={JSON.stringify(['PREORDER'])}>{t('aide.预售')}</Radio>
                    </Radio.Group>
                  );
                }}
              </FormField>
            </ItemArea>
          </Item>
        )}
      </Items>
    </Container>
  );
}

const Container = styled.div`
  background: var(--fett-bg-panel);
  border: 1px solid var(--fett-border-default);
  margin-bottom: 9px;
  .ant-row.ant-form-item {
    margin-bottom: 0;
  }
  .ant-form-item-control-input {
    min-height: 22px;
  }
`;

const Header = styled.div`
  border-bottom: 1px solid var(--fett-border-default);
  //display: flex;
  display: none;
  height: 40px;
  padding: 0 14px;
  align-items: center;
  justify-content: space-between;
  & > div {
    &:first-of-type {
      color: var(--fett-text-tertiary);
      font-size: 14px;
    }
    &:last-of-type {
      display: flex;
      align-items: center;
      & > span {
        font-size: 14px;
        color: var(--fett-text-secondary);
        margin-left: 4px;
        cursor: pointer;
      }
    }
  }
`;

const Items = styled.div`
  padding: 17px;
`;

const Item = styled.div`
  display: flex;
  align-items: flex-start;
  margin-bottom: 16px;
  overflow: hidden;
`;

const ItemTitle = styled.div`
  color: var(--fett-text-secondary);
  line-height: 20px;
  font-size: 14px;
  width: fit-content;
  flex-shrink: 0;
`;

const ItemArea = styled.div`
  overflow: hidden;
`;

const SpuSelector = styled.div``;

const selectInfoCss =
  'w-[414px] h-[47px] flex items-center p-[10px] bg-[var(--fett-erp-card-bg)] mt-[6px]';

export default ConfigurationItem;
